import Button from '@/common/components/Button'
import { FeeTextField } from '@/common/components/FeeTextField'
import SelectController from '@/common/components/SelectController'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { unFormatCurrency, unFormatPercentage } from '@/common/utils/unFormatters'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer'
import { useFeeTemplates } from '@/features/feeTemplates/hooks/useFeeTemplates'
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type'
import { usePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLead'
import { useUpdatePublicCompanyLeadSpecialFee } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadSpecialFee'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { SpecialEntriesData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { Dispatch, SetStateAction } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface SpecialFeeItemProps {
  specialEntry: SpecialEntriesData
  setSpecialEntries: Dispatch<SetStateAction<Partial<SpecialEntriesData>[]>>
  index: number
  createPublicCompanyLeadSpecialFee?: UseMutateFunction<
    void,
    Error,
    { value: string; type: string; fee_template_id: string },
    unknown
  >
  createPublicCompanyLeadSpecialFeeIsPending?: boolean
  deleteSpecialEntry: (props: { id: string; temporary?: boolean }) => void
  disabled?: boolean
}

export const SpecialFeeItem = ({
  specialEntry,
  setSpecialEntries,
  index,
  createPublicCompanyLeadSpecialFee,
  createPublicCompanyLeadSpecialFeeIsPending,
  deleteSpecialEntry,
  disabled,
}: SpecialFeeItemProps) => {
  const { id } = useParams()
  const queryClient = useQueryClient()

  const [searchEstablishment, setSearchEstablishment] = useDebounce('')
  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('')

  const {
    control,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelUpdateValidationSchema>()

  const { isFetching: publicCompanyIsFetching } = usePublicCompanyLead(id || '', false)

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishment },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer({ valueFieldName: 'id' }),
    }),
  })

  const { data: feeTemplateOptions } = useFeeTemplates({
    filters: { search: searchFeeTemplate, category: 'establishment' },
    mapper: Mapper.mapToOptions<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { mutate: updatePublicCompanyLeadSpecialFee, isPending: updateSpecialFeeIsPending } =
    useUpdatePublicCompanyLeadSpecialFee({
      publicCompanyLeadId: id,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', id] })
      },
    })

  const submitNewTarget = ({ id, target_id, target_type }: { id: string; target_id: string; target_type: string }) => {
    updatePublicCompanyLeadSpecialFee({ id, target_id, target_type })
  }

  const submitNewValue = ({ id, value, calculate_as }: { id: string; value: string; calculate_as: string }) => {
    if (!/R\$|%/.test(value)) {
      return
    }

    const unFormatFunction = calculate_as === 'percentage' ? unFormatPercentage : unFormatCurrency
    value = value && `${unFormatFunction(value)}`

    updatePublicCompanyLeadSpecialFee({ id, value })
  }

  return (
    <div key={specialEntry.id} className="space-y-4">
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline-error"
          className="size-[54px] mt-auto"
          onClick={() => deleteSpecialEntry({ id: specialEntry.id, temporary: !specialEntry.fee_template })}
          disabled={updateSpecialFeeIsPending || publicCompanyIsFetching || disabled}
        >
          <XIcon />
        </Button>
        <SelectController
          control={control}
          name={`fee_rule.special_entries.${index}.target`}
          label="Estabelecimento"
          className="w-full"
          onInputChange={setSearchEstablishment}
          value={
            specialEntry.temporary_target_id
              ? { label: specialEntry.temporary_target_name, value: specialEntry.temporary_target_id }
              : specialEntry.target
              ? EstablishmentCompanyTransformer.optionTransformer()([specialEntry.target])[0]
              : {
                  label: 'Todos os estabelecimentos',
                  value: 'all',
                  type: 'Establishment::Company',
                }
          }
          triggerOnChange={(newValue: { label: string; value: string; type: string }) => {
            specialEntry.target === undefined
              ? setSpecialEntries((prev) => {
                  const newSpecialEntries = [...prev]
                  newSpecialEntries[index] = {
                    id: newValue.value,
                    temporary_target_id: newValue.value,
                    temporary_target_name: newValue.label,
                    temporary_target_type: newValue.type,
                  }
                  return newSpecialEntries
                })
              : submitNewTarget({
                  id: specialEntry.id,
                  target_id: newValue.value,
                  target_type: newValue.type,
                })
          }}
          options={establishmentOptions || []}
          isDisabled={updateSpecialFeeIsPending || publicCompanyIsFetching || disabled}
          isLoading={createPublicCompanyLeadSpecialFeeIsPending}
        />
      </div>

      <div className="flex gap-2">
        <SelectController
          control={control}
          label="Tarifa"
          className="w-full"
          name={`fee_rule.special_entries.${index}.fee_template_id`}
          value={{ label: specialEntry.fee_template?.display_name, value: specialEntry.fee_template?.id }}
          onInputChange={setSearchFeeTemplate}
          triggerOnChange={(newValue: { label: string; value: string }) => {
            specialEntry.temporary_target_id &&
              specialEntry.temporary_target_type &&
              createPublicCompanyLeadSpecialFee?.({
                value: specialEntry.temporary_target_id,
                type: specialEntry.temporary_target_type,
                fee_template_id: newValue.value,
              })
          }}
          options={feeTemplateOptions || []}
          isDisabled={!!specialEntry.target || updateSpecialFeeIsPending || publicCompanyIsFetching || disabled}
          isLoading={createPublicCompanyLeadSpecialFeeIsPending}
        />
        {specialEntry.fee_template && (
          <FeeTextField
            contractFee={specialEntry}
            fieldName={`fee_rule.special_entries.${index}.value`}
            submit={submitNewValue}
            disabled={updateSpecialFeeIsPending || publicCompanyIsFetching || disabled}
            errorMessage={errors?.fee_rule?.special_entries?.[index]?.value?.message}
            className="w-full"
          />
        )}
      </div>
    </div>
  )
}
